import { inject, Injectable } from '@angular/core';
import { ErrorCatcherService } from './errorCatcher.service';

import { NavController, Platform } from '@ionic/angular/standalone';

import { Initializable } from './app-init.service';
import { ConfigUserService } from './config-user.service';
import { ZendeskService } from './zendesk.service';
import { StatsService } from './stats.service';
import { StudentService } from './student.service';
import { UserErrorHandlerService } from './user-error-handler.service';

import { Student } from '../models/student';

import { ToasterService } from '@commons/services';
import { SiteOpener } from './site-opener.service';
import { FeatureFlagsService } from './feature-flags.service';

import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

import { Subject } from 'rxjs';
import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';

@Injectable({ providedIn: 'root' })
@EasyDebugDecorator
export class NavigationService implements Initializable {
  private readonly featureFlagsService = inject(FeatureFlagsService);

  student: Student = null;
  configUser: any;
  userLifeCycle: any;
  universalLink = null;
  /** External URL from a push notification received during cold start, to be opened by the splash page */
  pendingPushNotificationUrl: string | null = null;

  // PAGE LOAD EVENT
  timestamp = null;
  lastLink = null;

  private readonly routesRequiringLogin = [
    'profil',
    'documents',
    'achats',
    'edit-profil',
    'edit-email',
    'edit-password',
    'lecons',
    'dashboard',
    'series/erreurs',
    'series/examens',
    'series/thematiques',
    'notation',
  ];

  featuresLinks = ['zendesk'];

  iframeAlreadyOpen = false;

  queryStringOrigin = 'spa';

  linksToUrl = {
    cms: environment.cmsBase,
    legacy: environment.legacyBase,
    home: { path: '/', base: 'cms' },
    'avis-cms': { path: '/avis-envoituresimone', base: 'cms' },
    'offres-cms': { path: '/', base: 'cms' },
    blog: { path: '/blog', base: 'cms' },
    cgu: { path: '/cgu', base: 'cms' },
    'code-de-la-route': { path: '/code-de-la-route', base: 'cms' },
    'code-de-la-route-gratuit': {
      path: '/code-de-la-route-gratuit',
      base: 'cms',
    },
    'conduite-site': { path: '/conduite', base: 'legacy' },
    dossier: { path: '/dossier', base: 'legacy' },
    dossierConvocation: { path: '/dossier/convocation', base: 'legacy' },
    livret: { path: '/dossier#info-section', base: 'legacy' },
    'livret-apprentissage': { path: '/livret/userId', base: 'legacy' },
    'examen-code-nouveau': { path: '/examen-code/nouveau', base: 'legacy' },
    'examen-code-examens': { path: '/examen-code/examens', base: 'legacy' },
    evaluate: { path: '/lesson/lessonId/rating', base: 'legacy' },
    infos: { path: '/informations-pratiques', base: 'legacy' },
    acceptLearningContact: {
      path: '/evaluation-prealable/edit',
      base: 'legacy',
    },
    'lecons-conduite': { path: '/lecons-de-conduite', base: 'cms' },
    // 'lesson': {path: '/lesson/lessonId', base: 'legacy'},
    // 'lessons': {path: '/lessons', base: 'legacy'},
    // 'reserver-lecon': {path: '/lesson/nouvelle', base: 'legacy'},
    tunnel: { path: '/tunnel', base: 'cms' },
    tunnelInscription: { path: '/tunnel/bienvenue', base: 'cms' },
    tunnelOffer: { path: '/tunnel?offer_id=offerId', base: 'legacy' },
    tunnelPaiement: {
      path: '/tunnel/paiement?offer_id=offerId&quantity=offerQuantity',
      base: 'legacy',
    },
    tunnelPaiementRefonte: {
      path: '/tunnel-paiement/paiement?offer_id=offerId&quantity=offerQuantity&already_driven=alreadyDriven&zipcode=zipCode&origin=tunnelOrigin&tunnel_request=tunnelRequest&guest_uid=guestUid',
      base: 'legacy',
    },
    cpf: { path: '/demarches-et-informations/cpf-permis', base: 'cms' },
    'infos-cpf': { path: '/demarches-et-informations/cpf-permis', base: 'cms' },
    faq: { path: 'https://aide.envoituresimone.com/hc/fr', base: '' },
    'faq-driving-test': {
      path: 'https://aide.envoituresimone.com/hc/fr/categories/360000065705',
      base: '',
    },
    'our-cars': { path: 'https://voitures.envoituresimone.com', base: '' },
    supportNewRequest: {
      path: 'https://aide.envoituresimone.com/hc/fr/requests/new',
      base: '',
    },
    insExamen: { path: '/inscription-examen-code', base: 'cms' },
    insExamenOld: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000803069-Comment-s-inscrire-%C3%A0-l-examen-du-code-',
      base: '',
    },
    aideExamen: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360010451680-Comment-se-d%C3%A9roule-l-examen-du-code-',
      base: '',
    },
    aideExamenConduite: {
      path: '/s-inscrire-a-l-examen-pratique',
      base: 'cms',
    },
    demarchesAdministratives: {
      path: 'https://aide.envoituresimone.com/hc/fr/categories/360000107269-Mes-d%C3%A9marches-administratives',
      base: '',
    },
    demarchesPrefecture: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000786625',
      base: '',
    },
    questionsExamen: {
      path: 'https://evs-other.s3.eu-west-1.amazonaws.com/questions-examen-permis.pdf',
      base: '',
    },
    aideDocuments: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000760005-Quels-sont-les-documents-%C3%A0-apporter-le-jour-de-l-%C3%A9preuve-du-permis-de-conduire-',
      base: '',
    },
    aideSuppressionCompte: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000760249',
      base: '',
    },
    aideNEPH: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000760245-Qu-est-ce-que-le-NEPH-',
      base: '',
    },
    howNEPH: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000786625-Comment-faire-ma-demande-de-NEPH',
      base: '',
    },
    gouvNEPH: {
      path: 'https://permisdeconduire.ants.gouv.fr/demarches-en-ligne/inscription-examen-permis',
      base: '',
    },
    lostNEPH: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360000822845-J-ai-perdu-ou-je-ne-connais-pas-mon-NEPH-Mon-NEPH-est-inactif',
      base: '',
    },
    livretApprentissageAide: {
      path: '/permis-de-conduire/conseils-conduite/livret-d-apprentissage',
      base: 'cms',
    },
    paiement: { path: '/boutique/paiement/paiementId', base: 'legacy' },
    paiementCodeExamReservation: {
      path: '/boutique/paiement/offerId?device_and_path=deviceAndPath',
      base: 'legacy',
    },
    paiement1hour: {
      path: '/boutique/paiement/paiementId?quantity=1',
      base: 'legacy',
    },
    examRegalurization: {
      path: '/boutique/exam_regularization',
      base: 'legacy',
    },
    paiements: { path: '/boutique/paiements', base: 'legacy' },
    // 'parrainage': {path: '/parrainage/redirect?user_id=userId', base: 'legacy'},
    permis: { path: '/permis-de-conduire', base: 'cms' },
    permisEnDeuxMois: {
      path: '/permis-de-conduire/permis-en-2-mois',
      base: 'cms',
    },
    examDateRDVP: {
      path: '/permis-de-conduire/date-examen-permis-avec-rdvpermis',
      base: 'cms',
    },
    app: { path: '/app', base: 'cms' },
    'points-rdv': { path: '/points-de-rendez-vous', base: 'cms' },
    profile: { path: '/dossier/informations-personnelles', base: 'legacy' },
    regulariserPayment: {
      path: '/boutique/impayé/ppu/résoudre',
      base: 'legacy',
    },
    signOut: { path: '/user/delete_session', base: 'cms' },
    tarifs: { path: '/tarifs', base: 'cms' },
    penurie: {
      path: '/reserver-une-lecon-avec-en-voiture-simone',
      base: 'cms',
    },
    politiqueConfidentialite: {
      path: '/politique-confidentialite',
      base: 'cms',
    },
    drivingExamConvocationForm: {
      path: '/rdvp/evs-driving-exam-convocation-form.pdf',
      base: 'legacy',
    },
    identityJustificatives: {
      path: '/rdvp/evs-driving-exam-identity-documents-BER-05-2024.pdf',
      base: 'legacy',
    },
    aideProfil: { path: 'https://aide.envoituresimone.com/hc/fr', base: '' },
    myTrainingAccount: {
      path: 'https://www.moncompteformation.gouv.fr/espace-prive',
      base: '',
    },
    nephReject: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/16884861223058-Gestion-de-votre-num%C3%A9ro-NEPH-pr%C3%A9sent-dans-votre-attestation-d-inscription',
      base: '',
    },
    examAccompagnement: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/360010687900-Combien-co%C3%BBte-l-accompagnement-%C3%A0-l-examen-de-la-conduite',
      base: '',
    },
    faqDateExamen: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/27363942101650-Comment-sont-attribu%C3%A9es-les-dates-d-examen-Fonctionnement-et-d%C3%A9lais',
      base: '',
    },
    // GOOGLE
    programmeFormation: {
      path: 'https://drive.google.com/file/d/1fUEk0dhCQ8ixu5KonGsCMZL2NR93_sxW/view ',
      base: '',
    },
    'google-map': { path: 'https://www.google.com/maps/place/term', base: '' },
    // TYPEFORMS
    sadtypeform: {
      path: 'https://en-voiture-simone.typeform.com/to/hwsRjW',
      base: '',
    },
    happytypeform: {
      path: 'https://en-voiture-simone.typeform.com/to/K0AjFu',
      base: '',
    },
    exp5Typeform: {
      path: 'https://en-voiture-simone.typeform.com/to/xTzOJ6',
      base: '',
    },
    exp20Typeform: {
      path: 'https://en-voiture-simone.typeform.com/to/hftVWg',
      base: '',
    },
    situationTypefom: {
      path: 'https://en-voiture-simone.typeform.com/to/tsi3gz?uid=userId',
      base: '',
    },
    // TUNNELS
    cgv: { path: 'https://legacy.envoituresimone.com/sales_terms/show', base: '' },
    privacy_policy: {
      path: 'https://www.envoituresimone.com/politique-confidentialite',
      base: '',
    },
    // NOTIONS
    examenVideo: {
      path: 'https://www.notion.so/Vid-os-centres-d-examen-5aa4703ed7174af28c7d33fcd49e12e5',
      base: '',
    },
    eLearning: {
      path: 'https://enchanting-rain-725.notion.site/Mon-espace-e-learning-En-Voiture-Simone-b164fbeafa4749e0b54ad7fc6bf30565',
      base: '',
    },
    drivingExamWaitingTime: {
      path: 'https://enchanting-rain-725.notion.site/L-tat-faillit-ses-promesses-des-d-lais-inacceptables-pour-le-permis-de-conduire-57f8eb4ad40d4716b1f0be42b9988598',
      base: '',
    },
    examDelay: {
      path: 'https://enchanting-rain-725.notion.site/L-tat-faillit-ses-promesses-des-d-lais-inacceptables-pour-le-permis-de-conduire-57f8eb4ad40d4716b1f0be42b9988598',
      base: '',
    },
    // GOUV
    candidatGouv: {
      path: 'https://candidat.permisdeconduire.gouv.fr/',
      base: '',
    },
    // CODE ENGINE
    questionsVerifications: {
      path: 'https://code-enligne.fr/doc/questions-verifications-2018-banque-verifications-01_01_18-2.pdf',
      base: '',
    },
    // LEGIFRANCE
    legifranceArrete: {
      path: 'https://www.legifrance.gouv.fr/loda/id/JORFTEXT000033736411',
      base: '',
    },
    // BLOGS
    blogInsExam: {
      path: '/blog/tuto-sinscrire-a-lexamen-du-code-de-la-route',
      base: 'legacy',
    },
    blogConseilCode: {
      path: '/blog/nos-13-conseils-pour-reussir-son-code-de-la-route-du-premier-coup',
      base: 'legacy',
    },
    blogVitesseMeteo: {
      path: '/blog/vitesse-et-meteo-que-dit-le-code-de-la-route',
      base: 'legacy',
    },
    blogFautesPermis: {
      path: '/blog/permis-de-conduire-ces-fautes-eliminatoires-a-avoir-en-tete',
      base: 'legacy',
    },
    blog3Conseils: {
      path: '/permis-de-conduire/examen-permis-conduire/stress-permis',
      base: 'cms',
    },
    promodeconf: {
      path: '/blog/code-promo-deconfinement-100eur-offert-sur-le-pack-permis',
      base: 'legacy',
    },
    promosummer2020: {
      path: '/blog/code-promo-soldes-dete-50eur-offert-sur-le-pack-permis',
      base: 'legacy',
    },
    // CGAAC (dynamic url)
    cgaac: {
      path: 'https://evs-profilepics.s3.amazonaws.com/cgaacUrl',
      base: '',
    },
    convocationDriving: { path: 'convocationUrl', base: '' },
    insuranceFile: { path: 'insuranceFileUrl', base: '' },
    cerfaFile: { path: 'cerfaFileUrl', base: '' },
    youtubeTiktok: {
      path: 'https://www.youtube.com/playlist?list=PLrjycpUFXbQ4TA7XSOScS1up6po5gxxL6',
      base: '',
    },
    tiktok: { path: 'https://www.tiktok.com/@envoituresimone_fr', base: '' },
    zendeskContactPage: {
      path: 'https://aide.envoituresimone.com/hc/fr/articles/25115083993746-Contact?token=zendeskToken',
      base: '',
    },
    tutoPermisYoutube: {
      path: 'https://www.youtube.com/watch?v=cZCMSD6SA9Y',
      base: '',
    },
  };

  frozenWhitelistRoutes = [
    'dashboard',
    'code',
    'profil',
    'blog',
    'cgu',
    'cgaac',
    'documents',
    'dossier',
    'infos',
    'faq',
    'supportNewRequest',
    'aideExamen',
    'demarchesAdministratives',
    'paiement',
    'regulariserPayment',
    'tunnelpaiementv2/regulariser',
    'signOut',
    'blogInsExam',
    'blogConseilCode',
    'blogVitesseMeteo',
    'blogFautesPermis',
    'blog3Conseils',
  ];

  isCordova = false;

  urlToRedirectForIos = [
    'blog',
    'cgu',
    'infos',
    'tunnel',
    'tunnelOffer',
    'paiement',
    'permis',
    'regulariserPayment',
    'tarifs',
    'cgaac',
    'tunnelInscription',
  ];

  forceNoIframe = false;
  closedIframeObs: Subject<any> = new Subject<any>();
  closedIframeObs$ = this.closedIframeObs.asObservable();

  // Fullscreen media handling for Android back button
  closeFullscreenObs: Subject<void> = new Subject<void>();
  closeFullscreen$ = this.closeFullscreenObs.asObservable();

  userNavigation = [];

  whiteList: string[] = [
    'aide.envoituresimone',
    'typeform',
    'amazonaws',
    'enseignant.envoituresimone.com',
    'drive.google',
    'google',
    'notion.so',
    'notion.site',
    'legifrance.gouv.fr',
    'code-enligne',
    'youtube',
    'tiktok',
  ];
  initLaunched = false;

  constructor(
    public navController: NavController,
    public router: Router,
    private platformService: Platform,
    private studentService: StudentService,
    private configUserService: ConfigUserService,
    public siteOpener: SiteOpener,
    private statsService: StatsService,
    public toasterService: ToasterService,
    private platform: Platform,
    private errorCatcherService: ErrorCatcherService,
    private userErrorHandlerService: UserErrorHandlerService,
    private zendeskService: ZendeskService
  ) {
    this.isCordova = this.platformService.is('cordova');
    this.iframeAlreadyOpen = false;
    // Initialize SiteOpener with link lists in constructor
    this.siteOpener.linkList = this.linksToUrl;
    this.siteOpener.whiteList = this.whiteList;
  }

  async init() {
    // Keep onInit for backward compatibility with @Initialable decorator
    // but now it's essentially a no-op since initialization happens in constructor
    return new Promise((resolve) => {
      // Already initialized in constructor, just resolve
      resolve('Navigation done');
    });
  }

  async open(link: string, options?: any) {
    // Handle direct URLs (e.g., from URL Builder Service)
    if (options?.isDirectUrl) {
      if (this.platform.is('cordova')) {
        return this.siteOpener.open(link, this.student, {
          ...options,
          forceOutLink: true,
        });
      } else {
        window.open(link, options?.target || '_blank');
        return null;
      }
    }

    this.initLaunched = false;
    if (typeof link !== 'undefined') {
      this.student = this.studentService.student;
      this.configUser = await this.configUserService.getUserConfig(this.student);
      this.userLifeCycle = this.studentService.getUserLifeCycle(this.student);

      const currentRoute = this.router.url.substr(1);
      // if (link === 'offres' && (this.userLifeCycle.isUserGuest || this.userLifeCycle.isUserRegistered) && currentRoute !== 'conduite') {
      //   link = 'offres-cms';
      // }

      let forceInAppBrowser = false;
      if (!!options && !!options.forceOutLink && options.forceOutLink) {
        forceInAppBrowser = true;
      }

      // Centralised fork: redirect settlement payment to new Checkout.com flow when flag is on.
      // All callers (top-nav, impayes.page, etc.) benefit automatically.
      if (link === 'regulariserPayment' && this.featureFlagsService.isFeatureFlagActive('checkout_payment_new_flow')) {
        await this.navController.navigateRoot('tunnelpaiementv2/regulariser');
        return;
      }

      let sendToIframe = false;
      if (this.isCordova && !this.forceNoIframe) {
        sendToIframe = true;
      }
      // if (this.isCordova && this.platformService.is('ios')) {
      //   if (this.urlToRedirectForIos.includes(link)) {
      //     link = 'acceder-au-site';
      //   }
      // }
      // ######################################################
      // # DEBUG FOR DESKTOP TESTS - DO NOT PUT IN PRODUCTION #
      // ######################################################
      // sendToIframe = true;
      // ######################################################
      // # DEBUG FOR DESKTOP TESTS - DO NOT PUT IN PRODUCTION #
      // ######################################################
      if (this.featuresLinks.includes(link)) {
        if (link === 'zendesk') {
          this.timestamp = null;
          this.lastLink = null;
          this.statsService.send({
            name: 'page:view',
            payload: { page: '/zendesk' },
          });
          // const conversationTags = options?.conversationTags || [];
          // this.zendeskService.openZendeskMessaging(conversationTags);

          // On web, open a blank window BEFORE the async fetch to avoid popup blocker
          // (popup blockers block window.open() calls after async operations)
          let newWindow: Window | null = null;
          if (!this.platform.is('cordova')) {
            newWindow = window.open('', '_blank');
          }

          const token = await this.zendeskService.fetchZendeskMessagingUserJWT().toPromise();

          if (newWindow) {
            // Web: update the pre-opened window's location using existing config
            const url = this.linksToUrl['zendeskContactPage'].path.replace('zendeskToken', token);
            newWindow.location.href = url;
          } else {
            // Cordova: use the normal flow with InAppBrowser
            this.open('zendeskContactPage', {
              target: '_system',
              zendeskToken: token,
            });
          }
        }
        if (link === 'rating') {
          this.timestamp = null;
          this.lastLink = null;
          this.statsService.send({
            name: 'page:view',
            payload: { page: '/rating' },
          });
          this.activateRating();
          this.userNavigation.push({
            link: 'rating',
            target: 'app',
          });
        }
      } else {
        if (this.userLifeCycle.isFrozen && currentRoute !== 'gmap') {
          if (!this.frozenWhitelistRoutes.includes(link)) {
            if (!!this.student.subStatus && this.student.subStatus.includes('payment_failed')) {
              link = 'impayes';
            }
          }
        }
        if (
          (typeof this.linksToUrl[link] === 'undefined' ||
            (!!this.linksToUrl[link] &&
              !!this.linksToUrl[link].path &&
              typeof this.linksToUrl[link].path === 'undefined')) &&
          !forceInAppBrowser
        ) {
          if (!!options && !!options.forcedSiteOpener && options.forcedSiteOpener) {
            if (!!options && !!options.target && options.target === '_system') {
              sendToIframe = false;
            }
            if (this.isCordova && this.platform.is('ios')) {
              sendToIframe = false;
            }
            if (sendToIframe) {
              // console.log(link);
              if (!this.isCordova && link === 'signOut' && this.iframeAlreadyOpen) {
                this.iframeAlreadyOpen = false;
              }
              if (!this.iframeAlreadyOpen) {
                // this.userNavigation.push({
                //   link: link,
                //   target: 'iframe'
                // });
                this.iframeAlreadyOpen = true;
                // console.log('student => ', this.student)
                const securedUrl = await this.siteOpener.getSecuredUrl(link, this.student, options);
                if (!!securedUrl && !!securedUrl.errorMessage) {
                  this.userErrorHandlerService.addError({
                    criticity: 10,
                    service: 'getSecuredUrl',
                    platform: 'both',
                    data: securedUrl,
                    errorCode: 'nsgsu',
                  });
                  // this.sentryService.sendToSentry(`EVSERROR Navigation Service Open securedUrl ${JSON.stringify(securedUrl)}`, this.studentService.student);
                  return null;
                }
                // console.log('SECURED URL => ', securedUrl);
                if (!securedUrl) {
                  this.iframeAlreadyOpen = false;
                }
                return securedUrl;
              } else {
                return null;
              }
            } else {
              // console.log('link 2 => ', link);
              this.siteOpenerOpen(link, options);
            }
          }
          this.timestamp = Date.now();
          this.lastLink = link;
          // this.statsService.send({ name: 'page:view', payload: { page: link } });
          // console.log(link, JSON.stringify(options));
          this.navigationControllerOpen(link, options);
        } else {
          // si url part of external domain then force target system
          if (
            !!this.linksToUrl[link] &&
            !!this.linksToUrl[link].path &&
            this.siteOpener.checkWhiteList(this.linksToUrl[link].path)
          ) {
            if (!!options) {
              options.target = '_system';
            } else {
              options = { target: '_system' };
            }
          }
          if (!!options && !!options.target && options.target === '_system') {
            sendToIframe = false;
          }
          if (this.isCordova && this.platform.is('ios')) {
            sendToIframe = false;
          }
          if (sendToIframe) {
            if (!this.iframeAlreadyOpen) {
              this.iframeAlreadyOpen = true;
              await this.resetStickyToasters();
              // this.userNavigation.push({
              //   link: link,
              //   target: 'iframe'
              // });
              // console.log('student => ', this.student)
              const securedUrl = await this.siteOpener.getSecuredUrl(link, this.student, options);
              if (!!securedUrl && !!securedUrl.errorMessage) {
                this.userErrorHandlerService.addError({
                  criticity: 10,
                  service: 'getSecuredUrl',
                  platform: 'both',
                  data: securedUrl,
                  errorCode: 'nsgsu',
                });
                // this.sentryService.sendToSentry(`EVSERROR Navigation Service Open securedUrl ${JSON.stringify(securedUrl)}`, this.studentService.student);
                return null;
              }
              if (!securedUrl) {
                this.iframeAlreadyOpen = false;
              }
              return securedUrl;
            } else {
              return null;
            }
          } else {
            // console.log('link 1 => ', link);
            this.siteOpenerOpen(link, options);
          }
        }
      }
    } else {
      console.error(JSON.stringify('NavigationService: No link!'));
    }
  }

  async navigationControllerOpen(link: string, options?: any) {
    let allow = true;
    if (
      this.userNavigation.length > 0 &&
      !!this.userNavigation[this.userNavigation.length - 1].link &&
      this.userNavigation[this.userNavigation.length - 1].link === link
    ) {
      allow = false;
    }
    if (allow) {
      this.userNavigation.push({
        link: link,
        target: 'app',
      });
    }
    // console.log('navigationControllerOpen');
    let forceRoot = false;
    if (!!options) {
      if (!!options.forceRoot && options.forceRoot) {
        forceRoot = true;
      }
    }

    forceRoot = true; // temp but too much case fucked with navigateForward
    const navOptions = options?.replaceUrl ? { replaceUrl: true } : undefined;
    if (typeof link === 'string' && link !== '') {
      if (forceRoot) {
        this.navController.navigateRoot(link, navOptions);
      } else {
        this.navController.navigateForward(link, navOptions);
      }
    } else {
      this.navController.back();
    }
  }

  removeSeriesEntries(serieType: string, serieId: string) {
    if (!serieType || !serieId) {
      return;
    }
    const prefix = 'series/' + serieType + '/' + serieId + '/';
    this.userNavigation = this.userNavigation.filter((entry) => {
      if (!entry?.link) {
        return true;
      }
      if (!entry.link.startsWith(prefix)) {
        return true;
      }
      return entry.link.includes('/results/') || entry.link.includes('/last-results/');
    });
  }

  async siteOpenerOpen(link: string, options?: any) {
    // this.userNavigation.push({
    //   link: link,
    //   target: 'siteOpener'
    // });
    // console.log('options => ', options);
    if (!options) {
      // _blank: Opens in the InAppBrowser. (default for evs)
      // _system: Opens in the system's web browser.
      options = { target: '_blank' };
    }
    // Is that the right place?
    // If we want to count InAppView like an app view and not a web view, we have to count it here...
    // What about internal Navigation?
    this.timestamp = null;
    this.lastLink = null;

    let redirectLink = 'undefined';
    if (!!this.linksToUrl[link]) {
      let base = '';
      if (!!this.linksToUrl[link].base) {
        const baseKey = this.linksToUrl[link].base;
        base = this.linksToUrl[baseKey];
      }
      if (!!this.linksToUrl[link].path) {
        redirectLink = base + this.linksToUrl[link].path;
      }
    }
    this.statsService.send({
      name: 'page:redirect',
      payload: { pageRedirect: redirectLink },
    });
    this.siteOpener
      .open(link, this.student, options)
      .then((res) => {
        if (!!res && !!res.errorMessage) {
          if (this.errorCatcherService.getRow('ME003').message !== res.errorMessage) {
            this.userErrorHandlerService.addError({
              criticity: 10,
              service: 'siteOpener',
              platform: 'both',
              data: res,
              errorCode: 'nsso',
            });
            // this.sentryService.sendToSentry('NavigationService siteOpener: ' + JSON.stringify(res), this.studentService.student);
          }
        }
      })
      .catch((err) => {
        this.userErrorHandlerService.addError({
          criticity: 10,
          service: 'siteOpener',
          platform: 'both',
          data: err,
          errorCode: 'nsso',
        });
        // this.sentryService.sendToSentry('NavigationService siteOpener: ' + JSON.stringify(err), this.studentService.student);
      });
    let autologLaunched = false;
    this.siteOpener.return.subscribe(async (res) => {
      if (!!res && !!res.end && res.end.length > 0) {
        if (res.end[0] === 'boutique' && res.end[1] === 'paiement') {
          // reload paiement
        }
      }
      if (!!res && !!res.url && res.url.indexOf('terminer-mon-inscription') > -1) {
        this.open('terminer-mon-inscription');
        return;
      }
      if (!!res && !!res.url && res.url.indexOf('reservation-examen-code') > -1) {
        this.open('reservation-examen-code');
        return;
      }
      if (!!res && !!res.url && res.url.indexOf('mon-inscription-examen') > -1) {
        this.open('mon-inscription-examen');
        return;
      }
      if (!!res && !!res.url && !autologLaunched) {
        autologLaunched = true;
        await this.studentService.autoLogPromise(res.url);
      }
    });
  }

  async resetStickyToasters() {
    const stickyToasters = document.getElementsByClassName('evs_toaster_sticky');
    for (const el of <any>stickyToasters) {
      el.parentNode.removeChild(el);
    }
    if (!this.configUser || this.configUser == null) {
      this.configUser = await this.configUserService.getUserConfig(this.student);
    }
    if (!!this.configUser) {
      this.configUser.impayesStickyToasterShown = false;
    }
    await this.configUserService.setUserConfig(this.student, this.configUser);
  }

  activateRating() {
    // console.log('RATING TODO');
  }

  checkUserNavigation() {
    let backUrl = null;
    // on filtre les urls de tableau sur l'url courante
    const filteredNav = this.userNavigation.filter(
      (elt) => elt.link === this.router.url.substr(1) && elt.target === 'app'
    );
    // on récupère l'index de la dernière occurence dans le tableau de l'url courante
    const indexInArray = this.userNavigation.findIndex((elt) => elt === filteredNav[filteredNav.length - 1]);
    if (
      !!this.userNavigation &&
      !!this.userNavigation[indexInArray - 1] &&
      !!this.userNavigation[indexInArray - 1].link &&
      filteredNav.length > 0 &&
      indexInArray !== -1
    ) {
      backUrl = {
        url: this.userNavigation[indexInArray - 1].link,
      };
    }
    // console.log('this.userNavigation', this.userNavigation);
    // console.log('this.router.url.substr(1)', this.router.url.substr(1));
    // console.log('indexInArray', this.userNavigation.findIndex((elt) => elt === filteredNav[filteredNav.length - 1]));
    // console.log('backUrl', backUrl);
    return backUrl;
  }
}
